import { useEffect, useState } from "react";
import { Loader2, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bouton de demande d'accès au Carnet PRO (visible quand parcelle locked
 * et utilisateur sans accès direct).
 */
export default function RequestAccessButton({ parcelId, onSent }) {
  const [state, setState] = useState({ checked: false, request: null, loading: true });
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    authFetch(`${API_BASE}/access-requests/sent`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (!mounted) return;
        const existing = list.find(r => r.parcel_id === parcelId && r.status === "pending");
        setState({ checked: true, request: existing || null, loading: false });
      })
      .catch(() => mounted && setState({ checked: true, request: null, loading: false }));
    return () => { mounted = false; };
  }, [parcelId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parcel_id: parcelId, message: message.trim() || null }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Demande envoyée au propriétaire");
        setState({ checked: true, request: data, loading: false });
        setShowForm(false);
        setMessage("");
        onSent?.();
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch { toast.error("Erreur"); }
    setSubmitting(false);
  };

  const cancel = async () => {
    if (!state.request) return;
    if (!window.confirm("Annuler votre demande d'accès ?")) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${state.request.request_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Demande annulée");
        setState({ checked: true, request: null, loading: false });
      }
    } catch { toast.error("Erreur"); }
    setSubmitting(false);
  };

  if (state.loading) {
    return <Loader2 className="w-3.5 h-3.5 animate-spin mt-1" style={{ color: "#2D6A4F" }} />;
  }

  if (state.request) {
    return (
      <div className="space-y-1.5" data-testid="access-request-pending">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: "#D4A373" }}>
          <Clock className="w-3 h-3" /> Demande en attente du propriétaire
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[10px] text-red-600 hover:bg-red-50"
          onClick={cancel}
          disabled={submitting}
          data-testid="cancel-request-btn"
        >
          Annuler ma demande
        </Button>
      </div>
    );
  }

  if (!showForm) {
    return (
      <Button
        size="sm"
        className="w-full h-8 text-[11px] font-semibold rounded-lg gap-1.5 text-white"
        style={{ background: "#2D6A4F" }}
        onClick={() => setShowForm(true)}
        data-testid="request-access-btn"
      >
        <Send className="w-3.5 h-3.5" /> Demander l'accès au propriétaire
      </Button>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="access-request-form">
      <textarea
        rows={3}
        maxLength={500}
        placeholder="Message (optionnel) — qui êtes-vous, pourquoi cet accès ?"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full text-xs p-2 rounded-lg border border-black/10 bg-white resize-none"
        data-testid="request-message-input"
      />
      <div className="flex gap-1.5">
        <Button
          size="sm"
          className="flex-1 h-8 text-[11px] font-semibold rounded-lg text-white"
          style={{ background: "#2D6A4F" }}
          onClick={submit}
          disabled={submitting}
          data-testid="request-submit-btn"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Envoyer
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-[11px] rounded-lg"
          onClick={() => { setShowForm(false); setMessage(""); }}
          data-testid="request-cancel-btn"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}
